/**
 * WB English Bulldogs — cart reservation rules
 */
(function () {
  const DEPOSIT_HANDLES = ['english-bulldog-puppy-deposit', 'deposit'];
  const MICROCHIP_HANDLE = 'microchip-id-add-on';

  function getItemHandle(item) {
    if (item && item.handle) return item.handle;
    if (!item || !item.url) return '';
    const match = String(item.url).match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function getMicrochipHandle(root) {
    return (root && root.dataset.microchipHandle) || MICROCHIP_HANDLE;
  }

  function getItemVariantId(item) {
    if (item && item.key && String(item.key).includes(':')) {
      return String(item.key).split(':')[0];
    }
    return item && item.variant_id != null ? String(item.variant_id) : '';
  }

  function isDeposit(item) {
    return DEPOSIT_HANDLES.includes(getItemHandle(item));
  }

  function isMicrochip(item, root, variantId) {
    const handle = getMicrochipHandle(root);
    if (getItemHandle(item) === handle) return true;
    if (variantId && getItemVariantId(item) === String(variantId)) return true;
    return false;
  }

  function isPuppy(item, root, variantId) {
    return !isDeposit(item) && !isMicrochip(item, root, variantId);
  }

  function hasReservation(cart, root, variantId) {
    return cart.items.some(function (item) {
      return isPuppy(item, root, variantId) || isDeposit(item);
    });
  }

  function hasReservationContext(cart, root, variantId) {
    const depositCents = root ? Number(root.dataset.depositCents) || 0 : 0;
    return hasReservation(cart, root, variantId) || depositCents > 0;
  }

  function findMicrochip(cart, root, variantId) {
    return cart.items.find(function (item) {
      return isMicrochip(item, root, variantId);
    });
  }

  function parseMoneyFormat(format) {
    if (!format) return '';
    try {
      const parsed = JSON.parse(format);
      return typeof parsed === 'string' ? parsed : format;
    } catch (err) {
      return format;
    }
  }

  function formatMoney(cents, format) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';

    const moneyFormat = parseMoneyFormat(format);

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function' && moneyFormat) {
      return Shopify.formatMoney(value, moneyFormat);
    }

    const amount = (value / 100).toFixed(2);
    if (!moneyFormat) return '$' + amount.replace(/\.00$/, '');

    return moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(value / 100)))
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  function computeDueToday(cart, root, variantId) {
    const depositCents = root ? Number(root.dataset.depositCents) || 0 : 0;
    const microchipCents = root ? Number(root.dataset.microchipCents) || 0 : 0;
    const checkbox = root ? root.querySelector('.wb-pdc__microchip-check') : null;

    let reservationCents = 0;
    let microchipLineCents = 0;
    let hasReservationLine = false;

    cart.items.forEach(function (item) {
      if (isMicrochip(item, root, variantId)) {
        microchipLineCents = item.final_line_price;
        return;
      }

      if (isPuppy(item, root, variantId) || isDeposit(item)) {
        hasReservationLine = true;
        reservationCents += item.final_line_price;
      }
    });

    if (hasReservationLine) {
      return reservationCents + microchipLineCents;
    }

    if (depositCents > 0) {
      const microchipSelected = microchipLineCents > 0 || !!(checkbox && checkbox.checked);
      return depositCents + (microchipSelected ? microchipLineCents || microchipCents : 0);
    }

    return reservationCents + microchipLineCents;
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart fetch failed');
    return res.json();
  }

  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });

    let data = {};
    try {
      data = await res.json();
    } catch (err) {
      data = {};
    }

    if (!res.ok) {
      throw new Error(data.description || data.message || 'cart request failed');
    }

    return data;
  }

  async function updateCart(updates) {
    return cartRequest('/cart/update.js', { updates: updates });
  }

  async function changeCartLine(id, quantity) {
    return cartRequest('/cart/change.js', { id: String(id), quantity: quantity });
  }

  async function addVariant(variantId) {
    const variantIdStr = String(variantId);
    return cartRequest('/cart/add.js', { items: [{ id: variantIdStr, quantity: 1 }] });
  }

  async function setMicrochipInCart(variantId, shouldHave, root) {
    let cart = await fetchCart();
    const existing = findMicrochip(cart, root, variantId);

    if (shouldHave) {
      if (existing) return cart;

      try {
        await addVariant(variantId);
      } catch (addErr) {
        cart = await fetchCart();
        if (findMicrochip(cart, root, variantId)) return cart;
        throw addErr;
      }

      return fetchCart();
    }

    if (!existing) return cart;

    try {
      await changeCartLine(existing.key, 0);
    } catch (err) {
      await updateCart({ [existing.key]: 0 });
    }

    return fetchCart();
  }

  async function sanitizeCart(root) {
    const cart = await fetchCart();
    const updates = {};
    const variantId = root ? root.querySelector('.wb-pdc__microchip-check')?.dataset.variantId : null;
    const puppies = cart.items.filter(function (item) {
      return isPuppy(item, root, variantId);
    });
    const deposits = cart.items.filter(isDeposit);
    const microchips = cart.items.filter(function (item) {
      return isMicrochip(item, root, variantId);
    });
    const reserved = puppies.length > 0 || deposits.length > 0;

    if (puppies.length > 1) {
      puppies.slice(1).forEach(function (item) {
        updates[item.key] = 0;
      });
    }

    if (puppies.length > 0) {
      deposits.forEach(function (item) {
        updates[item.key] = 0;
      });
    }

    if (!reserved) {
      microchips.forEach(function (item) {
        updates[item.key] = 0;
      });
    }

    if (Object.keys(updates).length === 0) return false;

    await updateCart(updates);
    return true;
  }

  function updateCartBubble(count) {
    document.querySelectorAll('.cart-count-bubble span, [data-cart-count]').forEach(function (el) {
      el.textContent = count;
      const parent = el.closest('.cart-count-bubble');
      if (parent) parent.classList.toggle('hidden', count < 1);
    });
  }

  function refreshTotals(cart, root) {
    if (!root) return;
    const format = root.dataset.moneyFormat;
    const checkbox = root.querySelector('.wb-pdc__microchip-check');
    const variantId = checkbox ? checkbox.dataset.variantId : null;
    const microchip = findMicrochip(cart, root, variantId);
    const dueToday = computeDueToday(cart, root, variantId);

    root.querySelectorAll('[data-wb-cart-total]').forEach(function (el) {
      el.textContent = formatMoney(dueToday, format);
    });

    if (checkbox) {
      checkbox.checked = !!microchip;
    }

    updateCartBubble(cart.item_count);
  }

  function bindMicrochip(root) {
    const checkbox = root.querySelector('.wb-pdc__microchip-check');
    if (!checkbox) return;

    const variantId = checkbox.dataset.variantId;
    const errorEl = root.querySelector('[data-wb-microchip-error]');
    const panel = root.querySelector('[data-wb-pdc-microchip]');
    let busy = false;

    checkbox.addEventListener('change', async function () {
      if (busy) {
        checkbox.checked = !checkbox.checked;
        return;
      }

      const shouldHave = checkbox.checked;
      busy = true;
      checkbox.disabled = true;
      if (panel) {
        panel.classList.add('is-loading');
        panel.setAttribute('aria-busy', 'true');
      }
      if (errorEl) errorEl.hidden = true;

      try {
        const cartBefore = await fetchCart();
        if (!hasReservationContext(cartBefore, root, variantId)) {
          throw new Error('reservation required');
        }

        const cart = await setMicrochipInCart(variantId, shouldHave, root);
        refreshTotals(cart, root);
      } catch (err) {
        checkbox.checked = !shouldHave;
        if (errorEl) {
          errorEl.textContent = root.dataset.microchipError || 'Could not update your cart. Please try again.';
          errorEl.hidden = false;
        }
        try {
          const cart = await fetchCart();
          refreshTotals(cart, root);
        } catch (refreshErr) {
          /* noop */
        }
      } finally {
        checkbox.disabled = false;
        if (panel) {
          panel.classList.remove('is-loading');
          panel.removeAttribute('aria-busy');
        }
        busy = false;
      }
    });
  }

  function bindCheckoutGuard(browseUrl) {
    document.addEventListener('click', async function (e) {
      const link = e.target.closest('a[href="/checkout"], a[href*="/checkout"]');
      if (!link) return;

      e.preventDefault();
      const root = document.getElementById('wb-puppy-deposit-cart');
      const sanitized = await sanitizeCart(root);
      if (sanitized) {
        window.location.reload();
        return;
      }

      const variantId = root ? root.querySelector('.wb-pdc__microchip-check')?.dataset.variantId : null;
      const cart = await fetchCart();
      if (!hasReservation(cart, root, variantId)) {
        window.location.href = browseUrl || '/collections/available-english-bulldog-puppies';
        return;
      }

      window.location.href = '/checkout';
    });
  }

  let checkoutGuardBound = false;
  let microchipBound = false;

  async function init() {
    const root = document.getElementById('wb-puppy-deposit-cart');
    const needsSanitize = document.documentElement.dataset.wbCartSanitize === 'true';

    if (needsSanitize) {
      const changed = await sanitizeCart(root);
      if (changed) {
        window.location.reload();
        return;
      }
    }

    const browseUrl = root ? root.dataset.browseUrl : '/collections/available-english-bulldog-puppies';
    if (!checkoutGuardBound) {
      bindCheckoutGuard(browseUrl);
      checkoutGuardBound = true;
    }

    if (!root || microchipBound) return;

    bindMicrochip(root);
    microchipBound = true;

    try {
      const cart = await fetchCart();
      refreshTotals(cart, root);
    } catch (err) {
      /* noop */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.WBCartRules = { sanitizeCart: sanitizeCart, hasReservation: hasReservation };
})();
